import clsx from 'clsx';
import { useState } from 'react';
// import { useRouter } from 'next/router';
import { NextPage } from 'next';
import SEO from '@/components/head/Seo';
import Advertisers from '@/components/head/Advertisers';
import JsonSchemaPage from '@/components/jsonSchema/Page';
import JsonSchemaRating from '@/components/jsonSchema/Rating';
import envConfig from '@/config/environment';
import Nav from '@/components/navigation/Public';
import Hero from '@/components/heros/Calculator';
import Footer from '@/components/legal/Footer';
import SignUpForm from '@/components/forms/Newsletter';
import globalEvents from '@/utils/globalEvents';
import redirectUser from '@/utils/redirectUser';
import ReviewsModal from '@/components/reviews/ReviewsModal';
import PrimaryWidget from '../../features/Calculators/PrimaryWidget';
import SubscribingWidget from '../../features/Calculators/SubscribingWidget';
import QuestionPresetsModal from '../../features/Calculators/modals/QuestionPresets';
// import Advertisement from '../../features/Calculators/Advertisement';
import Dashboard, { Cell } from '../../features/Calculators/Dashboard';
import layoutStyles from '@/components/Layout/styles.module.scss';
import { titleize } from '@/utils/strings';
import Article from '@/features/resources/WeddingBudgetCalculator/Article';
import settings from '@/features/resources/WeddingBudgetCalculator/settings';
import budgetSettings from '@/features/resources/WeddingBudgetCalculator/budgets';
import { matt, emily } from '@/content/authors';

const IS_STAGING = envConfig.isStaging;
const IS_DEVELOPMENT = envConfig.isDevelopment;

// @ts-ignore
const Page: NextPage = () => {
  const authors = [emily];
  const reviewers = [matt];
  const [isSelectingBudget, setIsSelectingBudget] = useState(false);
  const [isPrimaryWidgetLoaded, setIsPrimaryWidgetLoaded] = useState(false);
  const [isViewingReviews, setIsViewingReviews] = useState(false);

  const reviews = settings.reviews || [];
  const primaryWidgetId =
    IS_DEVELOPMENT || IS_STAGING
      ? settings.widgets.primaryStaging
      : settings.widgets.primary;
  const doughnutWidgetId =
    IS_DEVELOPMENT || IS_STAGING
      ? settings.widgets.doughnutStaging
      : settings.widgets.doughnut;
  const listWidgetId =
    IS_DEVELOPMENT || IS_STAGING
      ? settings.widgets.listStaging
      : settings.widgets.list;
  const premadeBudgetOptions = budgetSettings.premadeBudgets || [];
  const questionBudgetAllocations =
    budgetSettings.questionBudgetAllocations || [];
  const desiredBudgetQuestionId = budgetSettings.desiredBudgetQuestion;

  const reviewScore = parseFloat(
    (
      reviews.reduce((acc, { score }) => (acc += score), 0) /
      Math.max(reviews.length, 1)
    ).toFixed(1)
  );

  // Apply premade budgets to form
  // and trigger form resubmit
  const handlePremadeBudgetSelection = (selection: string) => {
    const budget = premadeBudgetOptions.find(
      ({ value }) => value === selection
    );
    if (!budget) return;

    // Update desired budget
    const desiredBudgetAmount = parseFloat(budget.value);
    globalEvents.trigger(`questionupdatevalue.${desiredBudgetQuestionId}`, {
      detail: desiredBudgetAmount,
    });

    // Update questions portion of total
    // amount allocation for selected budget
    let totalBudget = 0;
    questionBudgetAllocations
      // smallest to largest amounts
      .sort(
        (a1, a2) =>
          getBudgetAmount(a1.amounts, budget.value) -
          getBudgetAmount(a2.amounts, budget.value)
      )
      .forEach(({ id: questionId, amounts: srcAmounts }, i, arr) => {
        const targetAmount = getBudgetAmount(srcAmounts, budget.value);
        let amount = Math.floor((targetAmount / 100) * desiredBudgetAmount);
        totalBudget += amount;

        // Add any remaing budget to largest amount
        if (i === arr.length - 1 && totalBudget < desiredBudgetAmount) {
          amount += desiredBudgetAmount - totalBudget;
        }

        globalEvents.trigger(`questionupdatevalue.${questionId}`, {
          detail: amount,
        });
      });

    // Saved & resubmit the form
    // before scrolling to primary widget
    requestAnimationFrame(() => {
      globalEvents.trigger('formsave', {
        detail: primaryWidgetId,
      });
      globalEvents.trigger('formresubmit', {
        detail: primaryWidgetId,
      });
      redirectUser('#primary');
    });

    // Close selection modal
    setIsSelectingBudget(false);
  };

  return (
    <>
      <SEO
        title={settings.title}
        description={settings.description}
        twitterImgSrc={`${envConfig.baseUrl}${settings.images.twitterPost}`}
        ogImageSrc={`${envConfig.baseUrl}${settings.images.openGraph}`}
      />
      <Advertisers />

      <div
        role="main"
        className={clsx(layoutStyles.main, '-bgc-white')}
        id="scroll-container"
      >
        <Nav />
        <Hero
          heading={settings.hero.heading}
          description={settings.hero.description}
          breadcrumbs={[
            {
              href: `/${settings.directory}`,
              text: titleize(settings.directory),
            },
            { text: 'Weddings' },
          ]}
          ctaText={settings.hero.ctaText}
          ctaButtonIcon="gift"
          ctaDisabled={!isPrimaryWidgetLoaded}
          featuredImgSrcsets={settings.hero.featuredImgSrcsets}
          featuredImgAlt={settings.hero.featuredImgAlt}
          features={settings.hero.features}
          reviews={reviews}
          onClick={() => setIsSelectingBudget(true)}
          onReviewsClick={() => setIsViewingReviews(true)}
        />

        <PrimaryWidget
          widgetId={primaryWidgetId}
          elementId="primary"
          advertisement={false}
          name="weddings"
          onLoad={() => setIsPrimaryWidgetLoaded(true)}
        />

        {/* Grid Results */}
        {/* @ts-ignore */}
        <Dashboard elementId="results">
          <Cell
            variant="wide"
            heading="Budget Categories"
            padding={false}
            description="See which categories are having the biggest impact on your budget."
          >
            <SubscribingWidget
              widgetId={doughnutWidgetId}
              reportingWidgetId={primaryWidgetId}
            />
          </Cell>
          <Cell
            heading="Ranked Costs"
            tooltip="Categories and their associated costs"
            padding={false}
          >
            <SubscribingWidget
              widgetId={listWidgetId}
              reportingWidgetId={primaryWidgetId}
            />
          </Cell>
          <>
            {/* <Cell variant="wideTwo"><Advertisement /></Cell>*/}
            {/* <Cell><Advertisement /></Cell> */}
          </>
        </Dashboard>

        <Article
          updatedAt={settings.updatedAt}
          createdAt={settings.createdAt}
          authors={authors}
          reviewers={reviewers}
        />

        <SignUpForm />
        <Footer />
      </div>

      <QuestionPresetsModal
        isVisible={isSelectingBudget}
        heading={settings.hero.ctaText}
        cta="Select Budget"
        options={premadeBudgetOptions}
        onClose={() => setIsSelectingBudget(false)}
        onSubmit={handlePremadeBudgetSelection}
      />

      <ReviewsModal
        isVisible={isViewingReviews}
        reviews={reviews}
        cta="Add Your Review"
        onClose={() => setIsViewingReviews(false)}
      />

      <JsonSchemaPage
        headline={settings.title}
        url={`/${settings.slug}`}
        // @ts-ignore
        type={settings?.jsonLd?.type}
        createdAt={settings.createdAt}
        updatedAt={settings.updatedAt}
        imgSrc={`${envConfig.baseUrl}${settings.images.openGraph}`}
        authors={authors}
        editors={reviewers}
      />

      {reviews && reviews.length ? (
        <JsonSchemaRating
          name={settings.heading}
          description={settings.description}
          ratingCount={reviews.length}
          ratingValue={reviewScore}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export default Page;

// Lookup any predefiend value
// for active budget budget or default
function getBudgetAmount(
  amounts: Record<string, number | undefined>,
  budget: string
): number {
  const amt = amounts[budget];
  const dftAmt = amounts.default !== undefined ? amounts.default : -1;
  return amt !== undefined ? amt : dftAmt;
}
